import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FaStar } from '@react-icons/all-files/fa/FaStar';
import { FaUmbrellaBeach } from '@react-icons/all-files/fa/FaUmbrellaBeach';
import { FaCalendarAlt } from '@react-icons/all-files/fa/FaCalendarAlt';
import { FaCog } from '@react-icons/all-files/fa/FaCog';
import { FaHome } from '@react-icons/all-files/fa/FaHome';
import Breadcrumb from '../../../../../commons/Breadcrumb';
import PageHeader from '../../../../../commons/PageHeader';
import PageForm, { FormField } from '../../../../../commons/PageForm';
import PageAlert, { usePageAlert } from '../../../../../commons/PageAlert';
import { useAppDispatch, useAppSelector } from '../../../../../store';
import { getEntity, createEntity, updateEntity, reset } from '../../../../../manager/dia-festivo.reducer';

const BACK = '/configuracion/calendario/feriados/dias-festivos';
const FERIADOS = '/configuracion/calendario/feriados';

interface DiaFestivoForm {
  id: string;
  fecha: string;
  descripcion: string;
}

const empty: DiaFestivoForm = {
  id: '',
  fecha: '',
  descripcion: '',
};

const fields: FormField<DiaFestivoForm>[] = [
  {
    name: 'id',
    label: 'ID',
    readOnly: true,
    editOnly: true,
  },
  {
    name: 'fecha',
    label: 'Fecha',
    type: 'date',
    required: true,
  },
  {
    name: 'descripcion',
    label: 'Descripción',
    required: true,
    placeholder: 'Ej: Año Nuevo, Día de la Constitución',
  },
];

function toDateInputValue(value?: string | null): string {
  if (!value) return '';
  return String(value).slice(0, 10);
}

export default function DiasFestivosFormPage() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id;
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const entity = useAppSelector(s => s.diaFestivo.entity) as any;
  const updating = useAppSelector(s => s.diaFestivo.updating);
  const updateSuccess = useAppSelector(s => s.diaFestivo.updateSuccess);
  const errorMessage = useAppSelector(s => s.diaFestivo.errorMessage);

  const [form, setForm] = useState<DiaFestivoForm>(empty);
  const [errors, setErrors] = useState<Partial<Record<keyof DiaFestivoForm, string>>>({});

  const { alert, showToast } = usePageAlert();

  useEffect(() => {
    if (!isNew) {
      dispatch(getEntity(id!));
    } else {
      dispatch(reset());
      setForm(empty);
    }
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isNew && entity?.id) {
      setForm({
        id: String(entity.id),
        fecha: toDateInputValue(entity.fecha),
        descripcion: entity.descripcion ?? '',
      });
    }
  }, [entity]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (updateSuccess) {
      dispatch(reset());
      navigate(BACK, {
        state: {
          successMsg: isNew
            ? 'Día festivo creado correctamente.'
            : 'Día festivo actualizado correctamente.',
        },
      });
    }
  }, [updateSuccess]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (errorMessage) showToast('error', errorMessage);
  }, [errorMessage, showToast]);

  const handleFieldChange = (name: keyof DiaFestivoForm, value: string) => {
    setForm(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: undefined }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errs: Partial<Record<keyof DiaFestivoForm, string>> = {};
    if (!form.fecha.trim()) errs.fecha = 'La fecha es requerida';
    if (!form.descripcion.trim()) errs.descripcion = 'La descripción es requerida';
    if (Object.keys(errs).length) { setErrors(errs); return; }

    const payload = {
      fecha: form.fecha,
      descripcion: form.descripcion.trim(),
      ...(isNew ? {} : { id: Number(form.id) }),
    };

    if (isNew) {
      dispatch(createEntity(payload));
    } else {
      dispatch(updateEntity(payload));
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <Breadcrumb items={[
        { label: 'Inicio', to: '/', icon: <FaHome /> },
        { label: 'Configuración', to: '/configuracion', icon: <FaCog /> },
        { label: 'Calendario', to: '/configuracion/calendario', icon: <FaCalendarAlt /> },
        { label: 'Feriados', to: FERIADOS, icon: <FaUmbrellaBeach /> },
        { label: 'Días Festivos', to: BACK, icon: <FaStar /> },
        { label: isNew ? 'Nuevo día festivo' : `Editar día festivo #${id}` },
      ]} />

      <PageHeader
        icon={<FaStar />}
        iconColor="#f59e0b"
        title={isNew ? 'Nuevo Día Festivo' : 'Editar Día Festivo'}
        subtitle={isNew
          ? 'Registra un día festivo en el catálogo'
          : `Modificando día festivo #${id}`}
      />

      {alert && <PageAlert alert={alert} />}

      <PageForm
        fields={fields}
        values={form}
        errors={errors}
        isNew={isNew}
        saving={updating}
        onFieldChange={handleFieldChange}
        onSubmit={handleSubmit}
        onCancel={() => navigate(BACK)}
        accentColor="#f59e0b"
      />
    </div>
  );
}
